
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type CreateChannelDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateChannel: (channel: {
    name: string;
    description: string;
    isAnnouncement: boolean;
    isTemporary: boolean;
    autoDeleteAfter?: number;
  }) => void;
};

export function CreateChannelDialog({ open, onOpenChange, onCreateChannel }: CreateChannelDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isAnnouncement, setIsAnnouncement] = useState(false);
  const [isTemporary, setIsTemporary] = useState(false);
  const [autoDeleteAfter, setAutoDeleteAfter] = useState<number | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsSubmitting(true);
    try {
      await onCreateChannel({
        name: name.trim(),
        description: description.trim(),
        isAnnouncement,
        isTemporary,
        autoDeleteAfter,
      });
      
      // Reset form
      setName("");
      setDescription("");
      setIsAnnouncement(false);
      setIsTemporary(false);
      setAutoDeleteAfter(undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Channel</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Channel Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter channel name"
                required
                maxLength={50}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What is this channel for?"
                rows={3}
                maxLength={200}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="isAnnouncement" className="cursor-pointer">
                Announcement Channel
                <p className="text-sm text-muted-foreground">
                  Only admins and moderators can post
                </p>
              </Label>
              <Switch
                id="isAnnouncement"
                checked={isAnnouncement}
                onCheckedChange={setIsAnnouncement}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="isTemporary" className="cursor-pointer">
                Temporary Channel
                <p className="text-sm text-muted-foreground">
                  Messages will be auto-deleted after a set time
                </p>
              </Label>
              <Switch
                id="isTemporary"
                checked={isTemporary}
                onCheckedChange={setIsTemporary}
              />
            </div>
            
            {isTemporary && (
              <div className="grid gap-2">
                <Label htmlFor="autoDeleteAfter">Auto-delete After</Label>
                <Select
                  value={autoDeleteAfter?.toString() || ""}
                  onValueChange={(value) => setAutoDeleteAfter(Number(value) || undefined)}
                >
                  <SelectTrigger id="autoDeleteAfter">
                    <SelectValue placeholder="Select auto-delete time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 hour</SelectItem>
                    <SelectItem value="6">6 hours</SelectItem>
                    <SelectItem value="12">12 hours</SelectItem>
                    <SelectItem value="24">24 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Channel"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
